import React, { ReactElement, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dictionary, HomeProgram, HomeschoolProgram } from 'nzk-react-components'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import getGatsbyImage from 'utils/getGatsbyImage'

const Wrapper = styled.form`
  position: relative;
  max-width: 420px;
  width: 90vw;
  margin: 0 auto;
  background: radial-gradient(ellipse at top, #5e2d8e 0%, #3a1259 60%, #2a0e42 100%);
  padding: 28px 20px 20px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(94, 45, 142, 0.3);
  border: 2px solid rgba(200, 160, 255, 0.2);
  color: #fff;
  box-sizing: border-box;


  h2, h3 {
    font-family: "Rammetto One", cursive;
    font-size: 1.4rem;
    margin: 0 0 16px;
    font-weight: bold;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 500px) {
    h2, h3 {
      font-size: 1.1rem;
    }
  }

  p {
    font-size: 1rem;
    color: #fff;
    margin: 0 0 14px;
    line-height: 1.4;
  }

  .field {
    margin-bottom: 14px;
    text-align: left;
  }

  .field--label {
    font-size: 14px;
    margin-bottom: 6px;
    opacity: 0.7;
  }

  .disclaimer {
    font-size: 11px;
    opacity: 0.7;
    margin-top: 10px;
  }
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;
`

const ModernInput = styled.input`
  font-size: 1rem;
  color: #fff;
  border-radius: 14px;
  padding: 14px 18px;
  background-color: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.15);
  width: 100%;
  outline: none;
  transition: all 0.25s ease;
  box-sizing: border-box;
  backdrop-filter: blur(4px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    border-color: #E839B2;
    background-color: rgba(255, 255, 255, 0.18);
    box-shadow: 0 0 0 3px rgba(232, 57, 178, 0.15);
  }
`

const ErrorMessage = styled.div`
  font-size: 13px;
  background-color: rgba(232, 57, 178, 0.25);
  border: 1px solid rgba(232, 57, 178, 0.5);
  color: #fff;
  margin-bottom: 6px;
  padding: 6px 10px;
  border-radius: 8px;
`

const UserTypeSelector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .title {
    font-family: "Rammetto One";
    margin: 0 0 8px;
    opacity: 0.5;
    font-size: 0.8rem;
  }
`

const UserTypeCards = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`

const UserTypeCard = styled.div<{ selected: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  gap: 8px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.25s ease;
  background: ${({ selected }) => selected ? "rgba(255, 255, 255, 0.18)" : "rgba(255, 255, 255, 0.06)"};
  border: 2px solid ${({ selected }) => selected ? "#E839B2" : "rgba(255, 255, 255, 0.12)"};
  backdrop-filter: blur(4px);
  box-shadow: ${({ selected }) => selected ? "0 0 12px rgba(232, 57, 178, 0.2)" : "none"};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: ${({ selected }) => selected ? "#E839B2" : "rgba(255, 255, 255, 0.3)"};
  }

  .icon {
    font-size: 1.5em;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .label {
    font-size: 0.7em;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.02em;
  }
`

const USER_TYPE_OPTIONS: Array<{ value: 'parent' | 'homeschooler' | 'teacher'; label: string; icon: ReactElement }> = [
  { value: 'parent', label: 'Parent', icon: <HomeProgram /> },
  { value: 'teacher', label: 'Teacher', icon: <Dictionary /> },
  { value: 'homeschooler', label: 'Homeschooler', icon: <HomeschoolProgram /> },
]

interface IField {
  nodeType: "FIELD"
  label?: string
  name: string
  placeholder?: string
  type: "text" | "email" | "userType"
  required?: boolean
  error?: string
}

interface IText {
  nodeType: "TEXT"
  type: "title" | "paragraph"
  text: string
}

type IFormElement = IField | IText

interface IProps {
  id?: string
  fields: IFormElement[]
  submitButton: any
  onSubmit: (details: any) => void
}

const Form = (props: IProps) => {
  const [fields, setFields] = useState(props.fields)
  const [type, setType] = useState('parent')
  const formRef = useRef<HTMLFormElement | null>(null)
  const { willLogoImage } = useStaticQuery(graphql`
    {
      willLogoImage: file(relativePath: { eq: "will-logo-purple.png" }) {
        childImageSharp {
          gatsbyImageData(placeholder: NONE, width: 120)
        }
      }
    }
  `)

  const renderTextElement = (el: IText, key: string) => {
    if (el.type === 'title') return <h2 key={key}>{el.text}</h2>
    if (el.type === 'paragraph') return <p key={key}>{el.text}</p>
  }

  const renderFieldElement = (el: IField, key: string) => {
    let c: any = null
    let label: any = null
    let error: any = null

    if (el.type === 'text') {
      c = <ModernInput type='text' name={el.name} placeholder={el.placeholder} />
    } else if (el.type === 'email') {
      c = <ModernInput type='email' name={el.name} placeholder={el.placeholder} />
    } else if (el.type === 'userType') {
      c = (
        <UserTypeSelector>
          <div className="title">I am a:</div>
          <UserTypeCards>
            {USER_TYPE_OPTIONS.map(option => (
              <UserTypeCard
                key={option.value}
                selected={type === option.value}
                onClick={() => setType(option.value)}
              >
                <span className="icon">{option.icon}</span>
                <span className="label">{option.label}</span>
              </UserTypeCard>
            ))}
          </UserTypeCards>
        </UserTypeSelector>
      )
    }

    if (el.error) error = <ErrorMessage>{el.error}</ErrorMessage>
    if (el.label && el.type !== 'userType') label = <div className='field--label'>{el.label}{el.required ? '*' : ''}</div>

    return (
      <div className='field' key={key}>
        {label}
        {error}
        {c}
      </div>
    )
  }

  const renderElement = (el: IFormElement, key: string) => {
    switch (el.nodeType) {
      case 'TEXT': return renderTextElement(el, key)
      case 'FIELD': return renderFieldElement(el, key)
      default: return null
    }
  }

  const validateField = (field: IField, value: string) => {
    if (field.required && !value) return 'Field required'
    if (field.type === 'email' &&
      !new RegExp(/^[\w\-.+]+@([\w-]+\.)+[\w-]{2,4}$/).test(value)) return 'Please enter a valid email.'
  }

  const onSubmit = () => {
    const fieldNames = props.fields.map((f: any) => f.name).filter(Boolean)
    if (!formRef.current) return

    const data = new FormData(formRef.current)
    const fields = props.fields.map((field: any) => {
      if (!field.name) return field
      const error = validateField(field, data.get(field.name)?.toString() || '')
      return { ...field, error }
    })
    const nbErrors = fields.filter(f => f.error).length
    if (nbErrors > 0) return setFields(fields)
    const values = fieldNames.reduce((acc, f) => {
      acc[f] = data.get(f)
      return acc
    }, {})
    props.onSubmit({ ...values, type })
  }

  return (
    <Wrapper ref={formRef} id={props.id}>
      {willLogoImage && (
        <div style={{ position: 'absolute', top: '12px', left: '12px', width: '50px', zIndex: 1 }}>
          <GatsbyImage image={getGatsbyImage(willLogoImage)} imgStyle={{ objectFit: 'contain' }} alt="Logo" />
        </div>
      )}
      <FieldsWrapper>
        {fields.map((f, i) => renderElement(f, `${i}`))}
      </FieldsWrapper>
      {props.submitButton && (
        <div onClick={onSubmit} role="button" tabIndex={0} onKeyPress={e => e.key === 'Enter' && onSubmit()}>
          {props.submitButton}
        </div>
      )}
      <p className='disclaimer'>
        By entering your email, you give Night Zookeeper permission to contact
        you about their product and services.
      </p>
    </Wrapper>
  )
}

Form.defaultProps = {
  id: undefined
}

export default Form
